import { useEffect, useRef } from 'react';

/**
 * The focus behaviour a dialog owes the keyboard, in one place.
 *
 * Three things, and the third is the one that was missing everywhere:
 *
 * 1. **Initial focus** moves into the dialog when it opens.
 * 2. **Escape closes it.**
 * 3. **Focus returns to whatever opened it** when it closes. Without this, closing
 *    a dialog drops focus onto `<body>`, and the next Tab starts again from the
 *    top of the page — so a keyboard user who opened a row's edit dialog from
 *    halfway down a list is returned to the skip link. It is invisible with a
 *    mouse, which is why it survives review.
 *
 * `trap` is the difference between the two kinds of dialog here. A modal traps
 * Tab in a loop, because everything behind it is unreachable by definition. The
 * correction panel does not: it sits beside the month grid and you are meant to
 * be able to Tab back out to the grid and pick another day, so it takes the
 * other three behaviours and leaves Tab alone.
 */
export const useDialog = ({
  onClose,
  trap,
}: {
  onClose: () => void;
  trap: boolean;
}): {
  containerRef: React.RefObject<HTMLElement | null>;
  initialFocusRef: React.RefObject<HTMLButtonElement | null>;
} => {
  const containerRef = useRef<HTMLElement>(null);
  const initialFocusRef = useRef<HTMLButtonElement>(null);
  // Captured in a ref rather than read at cleanup time: by the time the effect
  // tears down, the element that had focus is usually already unmounted.
  const restoreTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    restoreTo.current = document.activeElement as HTMLElement | null;
    initialFocusRef.current?.focus();

    return () => {
      // isConnected: the opener may itself have been removed — deleting a row
      // from a dialog opened by that row's button is the ordinary case.
      if (restoreTo.current?.isConnected) restoreTo.current.focus();
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // A dialog opened from inside this one, or over it, takes the keys: Escape
      // closes the one on top, and Tab is that one's to trap.
      if (opensAbove(containerRef.current)) return;

      if (event.key === 'Escape') {
        onClose();

        return;
      }

      if (!trap || event.key !== 'Tab') return;

      const focusable = focusableWithin(containerRef.current);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;

      // Wrapping by hand, because a modal has no "outside" to Tab into. Focus
      // that leaves the dialog lands on page content the dialog is covering.
      if (event.shiftKey && (active === first || !containerRef.current?.contains(active))) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && active === last) {
        event.preventDefault();
        first.focus();
      }
    };

    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, trap]);

  return { containerRef, initialFocusRef };
};

/**
 * Whether another modal dialog stands above this one: one later in the
 * document, or inside it — a drawer that opens the banknote counter.
 */
const opensAbove = (container: HTMLElement | null): boolean => {
  if (!container) return false;

  return [...document.querySelectorAll<HTMLElement>('[aria-modal="true"]')].some(
    (dialog) =>
      dialog !== container && (container.compareDocumentPosition(dialog) & Node.DOCUMENT_POSITION_FOLLOWING) !== 0,
  );
};

/**
 * Focusable descendants, in document order.
 *
 * Queried on every Tab rather than cached: dialogs here change shape as they go —
 * a confirmation replaces the footer buttons, a save error adds an alert — and a
 * list captured when the dialog opened would send Tab to a removed node.
 */
const focusableWithin = (container: HTMLElement | null): HTMLElement[] => {
  if (!container) return [];

  const candidates = container.querySelectorAll<HTMLElement>(
    'a[href], button, input, select, textarea, [tabindex]',
  );

  return [...candidates].filter(
    (element) =>
      !element.hasAttribute('disabled') &&
      element.getAttribute('tabindex') !== '-1' &&
      // offsetParent is null for anything display:none — the cheap visibility
      // test. A folded `Collapse` is laid out but inert, and focus() on it does
      // nothing: Tab must not wrap to it.
      element.offsetParent !== null &&
      element.closest('[inert]') === null,
  );
};
